import React, {ReactElement, ReactNode, SyntheticEvent, useEffect, useRef, useState} from "react";
import {useResources} from "../../../stores/ResourceProvider";
import {Box, Button, FormLabel, IconButton, Typography, useTheme} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Model, {BudgetType, ModelSetting} from "../../../model/Model";
import Income, {IncomeType} from "../../../model/Income";
import IncomeCard from "./IncomeCard";
import Person from "../../../model/Person";
import ExpenseCard from "./ExpenseCard";
import Expense, {ExpenseCategory} from "../../../model/Expense";
import {
  arrayMove,
  createUUID,
  dateToLocalFormat,
  getDateToday,
  getISODateStartOfYear,
  getISODateToday,
  numberToMoneyFormat
} from "../../../stores/StoreUtilities";
import Schedule, {ScheduleNames} from "../../../model/Schedule";
import Deduction from "../../../model/Deduction";
import Tax, {TaxType} from "../../../model/Tax";
import DeductionCard from "./DeductionCard";
import TaxCard from "./TaxCard";
import BasicMenuButton from "../../../components/controls/BasicMenuButton";
import {AddCircleRounded} from "@mui/icons-material";
import Section from "../../../components/controls/Section";
import {
  IBudgetCalc,
  IDeductionItemSummary,
  IExpenseCategorySummary,
  IExpenseItemSummary,
  IFutureYear,
  IIncomeItemSummary,
  IPersonTimeline,
  ITaxItemSummary
} from "../../../components/calculator/Calculator";
import range from "lodash.range"
import MonthlyBudgetApexChart from "./charts/MonthlyBudgetApexChart";
import AnnualBudgetApexChart from "./charts/AnnualBudgetApexChart";
import MonthBudgetApexChart from "./charts/MonthBudgetApexChart";
import Asset, {AssetCategory} from "../../../model/Asset";
import YearSlider from "../../../components/controls/YearSlider";
import MonthSlider from "../../../components/controls/MonthSlider";
import ViewMenu from "../../../components/controls/ViewMenu";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreDoubleIcon from "@mui/icons-material/UnfoldMoreDouble";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {DragDropContext, DraggableProvided, DroppableProvided, DropResult} from "react-beautiful-dnd";
import DraggableList from "../../../components/controls/DraggableList";
import ModelStore from "../../../stores/ModelStore";
import {
  UpdateDeductionInput,
  UpdateExpenseInput,
  UpdateIncomeInput,
  UpdateTaxInput,
  UpdateUserSettingInput
} from "../../../API";
import Tracking from "../../../components/Tracking";
import IModelItem from "../../../model/IModelItem";
import BudgetReport from "./charts/BudgetReport";
import {useReactToPrint} from "react-to-print";
import PrintReport from "../../../components/page/PrintReport";
import BudgetHistoryChart from "./charts/BudgetHistoryChart";
import Snapshot from "../../../model/Snapshot";
import IBudget from "../../../model/iBudget";
import UserSetting, {IQuickStartSettings, UserSettingType} from "../../../model/UserSetting";
import BudgetQuickStart from "./BudgetQuickStart";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {useNavigate} from "react-router-dom";
import RoutesConfig from "../../../RoutesConfig";
import useIsMobile from "../../../components/useIsMobile";
import BarChartIcon from '@mui/icons-material/BarChart';
import AnnualBudgetWidget from "../dashboard/widgets/AnnualBudgetWidget";
import MobileDrawerButton from "../../../components/controls/MobileDrawerButton";
import {AddButtonIcon} from "../../../styles/Icons";
import ProjectionErrorAlert from "../ProjectionErrorAlert";

export const incomeMenuOptions = [
  new Income({description: "Paycheck (wages, salary or tips)", incomeType: IncomeType.WagesSalariesTips}),
  new Income({description: "Taxable Interest", incomeType: IncomeType.TaxableInterest}),
  new Income({description: "Tax Exempt Interest", incomeType: IncomeType.TaxExemptInterest}),
  new Income({description: "Qualified Dividends", incomeType: IncomeType.QualifiedDividends}),
  new Income({description: "Nonqualified Dividends", incomeType: IncomeType.NonqualifiedDividends}),
  new Income({description: "Pensions & Annuities (taxable)", incomeType: IncomeType.PensionsAndAnnuitiesTaxable}),
  new Income({description: "Pensions & Annuities (non-taxable)", incomeType: IncomeType.PensionsAndAnnuitiesNonTaxable}),
  new Income({description: "Social Security Benefit", incomeType: IncomeType.SocialSecurityBenefits}),
  new Income({description: "Social Security Child Benefit", incomeType: IncomeType.SocialSecurityChildBenefits}),
  new Income({description: "Short Term Capital Gains", incomeType: IncomeType.ShortTermCapitalGainsOrLoss}),
  new Income({description: "Long Term Capital Gains", incomeType: IncomeType.LongTermCapitalGainsOrLoss}),
  new Income({description: "Other Income", incomeType: IncomeType.OtherIncome}),
]

export const taxMenuOptions = [
  new Tax({description: "Federal Income Tax", taxType: TaxType.FederalIncomeTax}),
  new Tax({description: "Social Security/FICA (Employee)", taxType: TaxType.SocialSecurityTax}),
  new Tax({description: "Medicare (Employee)", taxType: TaxType.MedicareTax}),
  new Tax({description: "State Income Tax", taxType: TaxType.StateIncomeTax}),
  new Tax({description: "Local Income Tax", taxType: TaxType.LocalIncomeTax}),
  new Tax({description: "Other Income Tax", taxType: TaxType.OtherTax}),
]

export const essentialExpenseOptions = [
  new Expense({description: "Housing", expenseCategory: ExpenseCategory.Housing, discretionary: false, tip: "Rent or mortgage payments and related utilities (electricity, water, heating)"}),
  new Expense({description: "Food", expenseCategory: ExpenseCategory.FoodAndPersonalCare, discretionary: false, tip: "Groceries, restaurants, snacks"}),
  new Expense({description: "Clothing", expenseCategory: ExpenseCategory.FoodAndPersonalCare, discretionary: false, tip: "Clothing, shoes, accessories"}),
  new Expense({description: "Personal Care", expenseCategory: ExpenseCategory.FoodAndPersonalCare, discretionary: false, tip: "Haircuts, nails, hygiene products"}),
  new Expense({description: "Transportation", expenseCategory: ExpenseCategory.VehiclesAndTransportation, discretionary: false, tip: "Gas, oil, maintenance, repairs, public transportation, ride sharing"}),
  new Expense({description: "Healthcare", expenseCategory: ExpenseCategory.InsuranceAndMedical, discretionary: false, tip: "Medical, dental, vision, physical therapy"}),
  new Expense({description: "Communication", expenseCategory: ExpenseCategory.General, discretionary: false, tip: "Phone, internet, cable, satellite"}),
  new Expense({description: "Loan or Liability", expenseCategory: ExpenseCategory.LoansAndLiabilities, discretionary: false, tip: "credit card, car loan,student loan"}),
  new Expense({description: "Other Essential", expenseCategory: ExpenseCategory.General, discretionary: false, tip: "Other essential expenses"}),
  new Expense({description: "All Essential", expenseCategory: ExpenseCategory.General, discretionary: false, tip: "All essential expenses combined"}),
]

export const discretionaryExpenseOptions = [
  new Expense({description: "Entertainment", expenseCategory: ExpenseCategory.TravelAndEntertainment, discretionary: true, tip: "Movies, concerts, theater, sports"}),
  new Expense({description: "Travel", expenseCategory: ExpenseCategory.TravelAndEntertainment, discretionary: true, tip: "Airfare, hotel, car rental, other travel expenses"}),
  new Expense({description: "Giving", expenseCategory: ExpenseCategory.GivingAndMisc, discretionary: true, tip: "Charitable donations, tithing, gifts"}),
  new Expense({description: "Miscellaneous", expenseCategory: ExpenseCategory.GivingAndMisc, discretionary: true, tip: "Other discretionary expenses"}),
  new Expense({description: "All Discretionary", expenseCategory: ExpenseCategory.General, discretionary: true, tip: "All discretionary expenses combined"}),
]

export const generalExpenseOptions = [
  new Expense({description: "Essential Expenses", expenseCategory: ExpenseCategory.General, discretionary: false}),
  new Expense({description: "Discretionary Expenses", expenseCategory: ExpenseCategory.General, discretionary: true}),
]

export const housingExpenseOptions = [
  new Expense({description: "Cable", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Cellular Phone", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Electric", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Furniture", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Garbage", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "HOA Dues", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Home Improvements", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Home Phone", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Internet", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Natural Gas", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Real Estate Taxes", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Rent", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Security System", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Sewer", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Water", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Yard Maintenance / Gardening", expenseCategory: ExpenseCategory.Housing}),
  new Expense({description: "Other", expenseCategory: ExpenseCategory.Housing}),
]

export const loanAndLiabilityExpenseOptions = [
  new Expense({description: "Alimony", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "Auto Loan(s)", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "Boat Loan", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "Child Support", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "Credit Card", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "House Mortgage (P&I)", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "RV / Camping trailer loan", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "Student Loan", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
  new Expense({description: "Other", expenseCategory: ExpenseCategory.LoansAndLiabilities}),
]

export const foodAndPersonalCareExpenseOptions = [
  new Expense({description: "Chiropractor", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Clothes & Shoes", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Dry Cleaning", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Groceries", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Gym membership", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Haircuts / Nails / Massage", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Pet Food & Medication", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Restaurants", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Spending Cash", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
  new Expense({description: "Other", expenseCategory: ExpenseCategory.FoodAndPersonalCare}),
]

export const insuranceAndMedicalExpenseOptions = [
  new Expense({description: "Auto Insurance", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Dental Insurance", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Health Insurance", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Home Owners Insurance", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Life Insurance", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Long Term Care Insurance", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Medicare Part B", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Medicare Part D", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Medicare Supplement Insurance", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Medicare Advantage Plan", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Medications", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Vision & Eyecare", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
  new Expense({description: "Other", expenseCategory: ExpenseCategory.InsuranceAndMedical}),
]

export const vehiclesAndTransportationExpenseOptions = [
  new Expense({description: "Annual Tune Up", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Fuel", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Oil Change(s)", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "License Renewal Tabs", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Maintenance", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Memberships", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Public Transportation", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Repairs", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Tires", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
  new Expense({description: "Other", expenseCategory: ExpenseCategory.VehiclesAndTransportation}),
]

export const travelAndEntertainmentExpenseOptions = [
  new Expense({description: "Amazon Prime", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Anniversary", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Birthdays", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Christmas", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Dates", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Hobbies & Lessons ", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Magazine & Newspaper", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Netflix", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Software Subscriptions", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Vacations", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
  new Expense({description: "Other", expenseCategory: ExpenseCategory.TravelAndEntertainment}),
]

export const givingAndMiscExpenseOptions = [
  new Expense({description: "Charitable Donations", expenseCategory: ExpenseCategory.GivingAndMisc}),
  new Expense({description: "Financial Adviser", expenseCategory: ExpenseCategory.GivingAndMisc}),
  new Expense({description: "Missions", expenseCategory: ExpenseCategory.GivingAndMisc}),
  new Expense({description: "Tax Preparation", expenseCategory: ExpenseCategory.GivingAndMisc}),
  new Expense({description: "Tithe & Offerings", expenseCategory: ExpenseCategory.GivingAndMisc}),
  new Expense({description: "Other", expenseCategory: ExpenseCategory.GivingAndMisc}),
]

enum NetIncomeView {
  Annual,
  MonthlyAvg
}

const NetIncomeViewNames: string[] = [
  'Annual Surplus/Shortfall',
  'Monthly Surplus/Shortfall'
]

enum BudgetChartView {
  AnnualBudget,
  MonthBudget,
  MonthlyBudget,
  BudgetReport,
  BudgetHistory,
  QuickStart
}

const BudgetChartNames: string[] = [
  "Annual Budget",
  "Month Budget",
  "Monthly Budget",
  "Budget Report",
  "Budget History"
]

export const BudgetTypeOptions = [
  "Basic Budget",
  "Detailed Budget"
]

export enum BudgetSection {
  Income = "Income",
  Deductions = "Deductions",
  Taxes = "Taxes",
  EssentialExpenses = "EssentialExpenses",
  DiscretionaryExpenses = "DiscretionaryExpenses",
  General = "General",
  Housing = "Housing",
  LoansAndLiabilities = "LoansAndLiabilities",
  FoodAndPersonal = "FoodAndPersonal",
  InsuranceAndMedical = "InsuranceAndMedical",
  VehiclesAndTransportation = "VehiclesAndTransportation",
  TravelAndEntertainment = "TravelAndEntertainment",
  GivingAndMisc = "GivingAndMisc"
}

interface IBudgetViewSettings {
  netIncomeView: NetIncomeView
  expandedSections: string[]
  expandedCards: string[]
  chartView: BudgetChartView
  year?: number
  month?: number
  showArchived?: boolean
  mobileChart?: BudgetChartView | -1
}

const BudgetView = ({
  model,
  updatedAt,
  width,
  onChange,
  toggleDrawer
} : {
  model: Model
  updatedAt: string
  width: number
  onChange?(item: IModelItem): void
  toggleDrawer: () => void
}) => {
  const isMobile = useIsMobile()
  const sectionCount = 10
  const [viewStorageKey, setViewStorageKey] = useState<string>("")
  const [currentModel, setCurrentModel] = useState<Model | undefined>()
  const [persons, setPersons] = useState<Person[]>([])
  const [timelines, setTimelines] = useState<(IPersonTimeline | null)[]>([])
  const [incomes, setIncomes] = useState<Income[]>([])
  const [deductions, setDeductions] = useState<Deduction[]>([])
  const [taxes, setTaxes] = useState<Tax[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [essentialExpenses, setEssentialExpenses] = useState<Expense[]>([])
  const [discretionaryExpenses, setDiscretionaryExpenses] = useState<Expense[]>([])
  const [generalExpenses, setGeneralExpenses] = useState<Expense[]>([])
  const [housingExpenses, setHousingExpenses] = useState<Expense[]>([])
  const [loanAndLiabilityExpenses, setLoanAndLiabilityExpenses] = useState<Expense[]>([])
  const [foodAndPersonalCareExpenses, setFoodAndPersonalCareExpenses] = useState<Expense[]>([])
  const [insuranceAndMedicalExpenses, setInsuranceAndMedicalExpenses] = useState<Expense[]>([])
  const [vehiclesAndTransportationExpenses, setVehiclesAndTransportationExpenses] = useState<Expense[]>([])
  const [travelAndEntertainmentExpenses, setTravelAndEntertainmentExpenses] = useState<Expense[]>([])
  const [givingAndMiscExpenses, setGivingAndMiscExpenses] = useState<Expense[]>([])
  const [currentYear, setCurrentYear] = useState<number>(0)
  const [currentMonth, setCurrentMonth] = useState<number | undefined>()
  const [startYear, setStartYear] = useState<number>(0)
  const [endYear, setEndYear] = useState<number>(0)
  const [currentBudgetCalc, setCurrentBudgetCalc] = useState<IBudgetCalc | undefined>()
  const [currentYearCalc, setCurrentYearCalc] = useState<IFutureYear | undefined>()
  const [thisYearCalc, setThisYearCalc] = useState<IFutureYear | undefined>()
  const [calcError, setCalcError] = useState<string | undefined>()
  const [totalIncome, setTotalIncome] = useState<number>(0)
  const [totalDeductions, setTotalDeductions] = useState<number>(0)
  const [totalTaxes, setTotalTaxes] = useState<number>(0)
  const [totalEssentialExpenses, setTotalEssentialExpenses] = useState<number>(0)
  const [totalDiscretionaryExpenses, setTotalDiscretionaryExpenses] = useState<number>(0)
  const [totalGeneral, setTotalGeneral] = useState<number>(0)
  const [totalHousing, setTotalHousing] = useState<number>(0)
  const [totalLoansAndLiabilities, setTotalLoansAndLiabilities] = useState<number>(0)
  const [totalFoodAndPersonal, setTotalFoodAndPersonal] = useState<number>(0)
  const [totalInsuranceAndMedical, setTotalInsuranceAndMedical] = useState<number>(0)
  const [totalVehiclesAndTransportation, setTotalVehiclesAndTransportation] = useState<number>(0)
  const [totalTravelAndEntertainment, setTotalTravelAndEntertainment] = useState<number>(0)
  const [totalGivingAndMisc, setTotalGivingAndMisc] = useState<number>(0)

  const [netIncomeView, setNetIncomeView] = useState<NetIncomeView>(NetIncomeView.Annual)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set<string>([]))
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set<string>([]))
  const [chartView, setChartView] = useState<BudgetChartView>(BudgetChartView.AnnualBudget)
  const [currentSnapshot, setCurrentSnapshot] = useState<Snapshot | undefined>()
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | undefined>()
  const [showArchived, setShowArchived] = useState<boolean>(false)
  const [budgetType, setBudgetType] = useState<BudgetType>(BudgetType.Detailed)
  const [mobileChart, setMobileChart] = useState<BudgetChartView | -1 | undefined>(BudgetChartView.AnnualBudget)
  const [isLocked, setIsLocked] = useState<boolean>(false)

  const [quickStartUserSetting, setQuickStartUserSetting] = useState<UserSetting | undefined>()
  const [quickStartStep, setQuickStartStep] = useState<string | undefined>()
  const [showQuickStart, setShowQuickStart] = useState<boolean>(true)

  const [isPrinting, setIsPrinting] = useState<boolean>(false)
  const printRef = useRef(null)
  const promiseResolveRef = useRef<any>(null)

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current();
    }
  }, [isPrinting]);

  const theme = useTheme()
  const navigate = useNavigate()
  const { modelStore, userStore, calculator } = useResources()

  useEffect(() => {
    // Scroll to top
    setTimeout(() => {
      window.scrollTo(0, 0)
    }, 200);
  }, [])

  useEffect(() => {
    if (model) {
      setCurrentModel(model)
      setCurrentSnapshot(model.snapshots[0])
      setSelectedSnapshot(model.snapshots[0])
      model.persons.sort((a: Person, b: Person) => a.createdAt.localeCompare(b.createdAt))
      setPersons(model.persons)
      const lines = model.persons.map((p: Person) => calculator.personTimeline((p)))
      setTimelines(lines)
      const startDate = getDateToday()
      let endDate = startDate
      if (lines.length >= 0 && lines[0]) {
        endDate = lines[0].lifeExpectancyDate
        if (lines.length >= 1 && lines[1]) {
          if (lines[1]?.lifeExpectancyDate.getTime() > endDate.getTime()) {
            endDate = lines[1]?.lifeExpectancyDate
          }
        }
      }
      setStartYear(startDate.getFullYear())
      setCurrentYear(startDate.getFullYear())
      setEndYear(endDate.getFullYear()+1)
      // setIncomes([...model.getPaychecks()])
      setIncomes(model.incomes)
      setDeductions(model.deductions)
      setTaxes(model.taxes)
      setExpenses(model.expenses)
      setIsLocked(model.isLocked())
    }

    // Load localStorage view settings
    loadSettings()
  }, [model, model.updatedAt, updatedAt])

  useEffect(() => {
      if (currentModel) {
        saveSettings()
      }
    }, [netIncomeView, expandedSections, expandedCards, chartView, mobileChart, currentYear, currentMonth, showArchived, viewStorageKey])

  useEffect(() => {
    if (currentModel && currentYear > 0) {
      // console.debug(`BudgetView.calculate: ${currentModel.name} ${currentYear}`)
      // currentModel.incomes = incomes
      // currentModel.deductions = deductions
      // currentModel.taxes = taxes
      // currentModel.expenses = expenses

      const futureCalc = calculator.calculateFuture(currentModel, "Budget View", currentYear)
      // The budget doesn't depend on the projection: when the projection can't be calculated, show its error, clear the
      // projected year values and total the budget on its own, so the budget stays usable for repairing the plan
      setCalcError(futureCalc.calcError)
      const futureYear = futureCalc.calcError ? undefined : futureCalc.futureYears[futureCalc.futureYears.length-1]
      setCurrentYearCalc(futureYear)
      const thisYear = futureCalc.calcError ? undefined : futureCalc.futureYears[0]
      setThisYearCalc(thisYear)
      const budgetCalc = futureYear ? futureYear.budgetCalc : calculator.calculateBudget(currentYear, currentModel)
      // const budgetCalc = calculator.calculateBudget(currentYear, model)
      setEssentialExpenses(expenses.filter((expense: Expense) => !expense.discretionary))
      setDiscretionaryExpenses(expenses.filter((expense: Expense) => expense.discretionary === true))
      setGeneralExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.General))
      setHousingExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.Housing))
      setLoanAndLiabilityExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.LoansAndLiabilities))
      setFoodAndPersonalCareExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.FoodAndPersonalCare))
      setInsuranceAndMedicalExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.InsuranceAndMedical))
      setVehiclesAndTransportationExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.VehiclesAndTransportation))
      setTravelAndEntertainmentExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.TravelAndEntertainment))
      setGivingAndMiscExpenses(expenses.filter((expense: Expense) => expense.expenseCategory === ExpenseCategory.GivingAndMisc))
      setCurrentBudgetCalc(budgetCalc)
      setTotalIncome(budgetCalc.totalIncome)
      setTotalDeductions(budgetCalc.totalDeductions)
      setTotalTaxes(budgetCalc.totalTaxes)
      setTotalEssentialExpenses(budgetCalc.totalEssentialExpenses)
      setTotalDiscretionaryExpenses(budgetCalc.totalDiscretionaryExpenses)
      // setTotalTaxes(thisYear.taxes)
      setTotalGeneral(getExpenseCategoryTotal(ExpenseCategory.General, budgetCalc))
      setTotalHousing(getExpenseCategoryTotal(ExpenseCategory.Housing, budgetCalc))
      setTotalLoansAndLiabilities(getExpenseCategoryTotal(ExpenseCategory.LoansAndLiabilities, budgetCalc))
      setTotalFoodAndPersonal(getExpenseCategoryTotal(ExpenseCategory.FoodAndPersonalCare, budgetCalc))
      setTotalInsuranceAndMedical(getExpenseCategoryTotal(ExpenseCategory.InsuranceAndMedical, budgetCalc))
      setTotalVehiclesAndTransportation(getExpenseCategoryTotal(ExpenseCategory.VehiclesAndTransportation, budgetCalc))
      setTotalTravelAndEntertainment(getExpenseCategoryTotal(ExpenseCategory.TravelAndEntertainment, budgetCalc))
      setTotalGivingAndMisc(getExpenseCategoryTotal(ExpenseCategory.GivingAndMisc, budgetCalc))
      setBudgetType(currentModel.getSetting(ModelSetting.BudgetType, BudgetType.Basic))
    }
  }, [currentModel?.updatedAt, incomes, deductions, taxes, expenses, currentYear, netIncomeView])

  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      color: theme.palette.grey["700"],
    },
    miniButton: {
      height: 16,
      minWidth: 0,
      marginTop: "2px",
      borderRadius: "5px"
    },
    miniButtonDisabled: {
      height: 16,
      minWidth: 0,
      borderRadius: "5px",
      marginTop: "2px",
      opacity: 0.6,
    },
  }

  const loadSettings = () => {
    let storageKey = viewStorageKey
    if (!storageKey) {
      storageKey = `${modelStore.currentUser?.id}.BudgetViewSettings`
      setViewStorageKey(storageKey)
    }
    const data = window.localStorage.getItem(storageKey)
    if (data) {
      const settings: IBudgetViewSettings = JSON.parse(data)
      setNetIncomeView(settings.netIncomeView ?? NetIncomeView.Annual)

      if (settings.expandedSections && settings.expandedSections.length && typeof settings.expandedSections[0] === "string") {
        setExpandedSections(new Set<string>([...settings.expandedSections]))
      } else {
        setAllSectionsExpanded(false)
      }
      if (settings.expandedCards && settings.expandedCards.length) {
        setExpandedCards(new Set<string>([...settings.expandedCards]))
      } else {
        setExpandedCards(new Set<string>([]))
      }

      setChartView(settings.chartView && settings.chartView <= BudgetChartView.BudgetHistory ? settings.chartView : BudgetChartView.AnnualBudget)
      if (settings.chartView === BudgetChartView.MonthBudget) {
        setCurrentMonth(settings.month ?? 0)
      }
      if (settings.year && settings.year >= getDateToday().getFullYear()) {
        setCurrentYear(settings.year)
      }
      if (settings.showArchived !== undefined) {
        setShowArchived(settings.showArchived)
      }
      if (settings.mobileChart === undefined) {
        setMobileChart(BudgetChartView.AnnualBudget)
      } else if (settings.mobileChart >= 0) {
          setMobileChart(settings.mobileChart)
      } else {
        setMobileChart(-1)
      }
    }

    const userSetting = userStore!.user!.getUserSetting(UserSettingType.quickStart, model.id)
    if (userSetting) {
      const quickStartSettings: IQuickStartSettings = userSetting.settings
      if (quickStartSettings && quickStartSettings.status === "Active" && quickStartSettings.view === "budget") {
        setShowQuickStart(true)
        setQuickStartUserSetting(userSetting)
        setQuickStartStep(quickStartSettings.step)
        setChartView(BudgetChartView.QuickStart)
      } else {
        setShowQuickStart(false)
      }
    } else {
      setShowQuickStart(false)
      setChartView(BudgetChartView.AnnualBudget)
    }
  }

  const saveSettings = () => {
    const settings = JSON.stringify({
      netIncomeView: netIncomeView,
      expandedSections: Array.from(expandedSections),
      expandedCards: Array.from(expandedCards),
      chartView: chartView,
      month: (chartView === BudgetChartView.MonthBudget) ? currentMonth : undefined,
      year: currentYear,
      showArchived: showArchived,
      mobileChart: mobileChart
    })
    window.localStorage.setItem(viewStorageKey, settings)
  }

  const getExpandedSection = (id: BudgetSection) => {
    return expandedSections.has(id)
  }

  const setExpandedSection = (id: BudgetSection, expand: boolean) => {
    if (expand) {
      expandedSections.add(id)
    } else {
      expandedSections.delete(id)
    }
    setExpandedSections(new Set(expandedSections))
  }

  const getAllSectionsExpanded = (): boolean => {
    return expandedSections.size === sectionCount
  }

  const setAllSectionsExpanded = (expanded: boolean) => {
    if (expanded) {
      setExpandedSections(new Set<string>(Object.values(BudgetSection)))
    } else {
      setExpandedSections(new Set<string>([]))
    }
  }

  const getExpandedCard = (id: string) => {
    const expanded = expandedCards.has(id)
    return expanded
  }

  const setExpandedCard = (id: string, expand: boolean) => {
    if (expand) {
      expandedCards.add(id)
    } else {
      expandedCards.delete(id)
    }
    setExpandedCards(new Set(expandedCards))
  }

  const getAllCardsExpanded = (): boolean => {
    const totalCards = incomes.length + deductions.length + taxes.length + expenses.length
    return (expandedCards.size === totalCards)
  }

  const setAllCardsExpanded = (expanded: boolean) => {
    if (expanded) {
      const set = new Set<string>([])
      incomes.forEach((item: Income) => set.add(item.id))
      deductions.forEach((item: Deduction) => set.add(item.id))
      taxes.forEach((item: Tax) => set.add(item.id))
      expenses.forEach((item: Expense) => set.add(item.id))
      setExpandedCards(set)
    } else {
      setExpandedCards(new Set<string>([]))
    }
  }


  const getSectionTotal = (amount: number): string => {
    let total
    if (netIncomeView === NetIncomeView.MonthlyAvg) {
      total = Math.round(amount / 12)
    } else {
      total = amount
    }
    return numberToMoneyFormat(total, 0)
  }

  const getProjectedIncome = (income: Income) => {
    const {id} = income

    let total = 0
    if (currentBudgetCalc) {
      const item = currentBudgetCalc.incomeSummary.byItem.find((item: IIncomeItemSummary) => item.id === id)
      if (item) {
        total = item.total
      }
    }
    if (netIncomeView === NetIncomeView.MonthlyAvg) {
      return Math.round(total / 12)
    } else {
      return total
    }
  }

  const getProjectedDeduction = (deduction: Deduction) => {
    const {id} = deduction

    let total = 0
    if (currentBudgetCalc) {
      const item = currentBudgetCalc.deductionSummary.byItem.find((item: IDeductionItemSummary) => item.id === id)
      if (item) {
        total = item.total
      }
    }
    if (netIncomeView === NetIncomeView.MonthlyAvg) {
      return Math.round(total / 12)
    } else {
      return total
    }
  }

  const getProjectedTax = (tax: Tax) => {
    const {id} = tax

    let total = 0
    if (currentBudgetCalc) {
      const item = currentBudgetCalc.taxSummary.byItem.find((item: ITaxItemSummary) => item.id === id)
      if (item) {
        total = item.total
      }
    }
    if (netIncomeView === NetIncomeView.MonthlyAvg) {
      return Math.round(total / 12)
    } else {
      return total
    }
  }

  const getProjectedExpense = (expense: Expense) => {
    const {id, expenseCategory} = expense
    
    let total = 0
    if (currentBudgetCalc) {
      const summary = currentBudgetCalc.expenseSummary.byCategory.find((summary: IExpenseCategorySummary) => summary.category === expenseCategory)
      if (summary) {
        const item = summary.byItem.find((item: IExpenseItemSummary) => item.id === id)
        if (item) {
          total = item.total
        }
      }
    }
    if (netIncomeView === NetIncomeView.MonthlyAvg) {
      return Math.round(total / 12)
    } else {
      return total
    }
  }

  const getExpenseCategoryTotal = (category: ExpenseCategory, budgetCalc?: IBudgetCalc) => {
    let total = 0
    if (budgetCalc) {
      const summary = budgetCalc.expenseSummary.byCategory.find((summary: IExpenseCategorySummary) => summary.category === category)
      if (summary) {
        total = summary.total
      }
    }
    return total
  }

  const handleAddIncome = (index: number) => {
    const option = incomeMenuOptions[index]
    if (currentModel && option) {
      const description = option.description.includes(" (") ? option.description.substring(0, option.description.indexOf(" (")) : option.description
      const income = new Income({
        id: createUUID(),
        accountId: currentModel.accountId,
        userId: currentModel.userId,
        modelId: currentModel.id,
        incomeType: option.incomeType,
        description: description,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: option.incomeType !== IncomeType.SocialSecurityBenefits ? getISODateStartOfYear() : undefined,
        amount: 0,
        amountDate: getISODateToday(),
        archived: false,
        sortOrder: incomes.length > 0 ? incomes[incomes.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setIncomes([...incomes, income])
      setExpandedSection(BudgetSection.Income, true)
      setExpandedCard(income.id, true)
    }
  }

  const handleSaveIncome = (income: Income) => {
    const index = incomes.findIndex((e: Income) => e.id === income.id)
    if (index >= 0) {
      incomes[index] = income
      setIncomes([...incomes])
    }

    if (onChange) {
      onChange(income)
    }
  }

  const handleDeleteIncome = (deleted: Income) => {
    const incomeArray: Income[] = [...incomes]
    for (let i = incomes.length-1; i >= 0; --i) {
      const income = incomes[i]
      if (income.id === deleted.id || income.paycheckId === deleted.id) {
        incomeArray.splice(i, 1)
      }
    }
    setIncomes([...incomeArray])

    const deductionArray: Deduction[] = [...deductions]
    for (let i = deductions.length-1; i >= 0; --i) {
      const deduction = deductions[i]
      if (deduction.paycheckId === deleted.id) {
        deductionArray.splice(i, 1)
      }
    }
    setDeductions([...deductionArray])

    const taxArray: Tax[] = [...taxes]
    for (let i = taxes.length-1; i >= 0; --i) {
      const tax = taxes[i]
      if (tax.paycheckId === deleted.id) {
        taxArray.splice(i, 1)
      }
    }
    setTaxes([...taxArray])

    if (onChange) {
      onChange(currentModel!)
    }
  }

  const handleAddDeduction = (index: number) => {
    const options = getDeductionMenuOptions()
    const option = options[index]

    if (currentModel && option) {
      const deduction = new Deduction({
        id: createUUID(),
        accountId: currentModel.accountId,
        userId: currentModel.userId,
        modelId: currentModel.id,
        description: option,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: getISODateStartOfYear(),
        amount: 0,
        amountDate: getISODateToday(),
        archived: false,
        sortOrder: deductions.length > 0 ? deductions[deductions.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      if (option !== 'Other' && currentModel) {
        const asset = currentModel.getAssetByDescription(option)
        if (asset) {
          deduction.assetId = asset.id
        }
      }
      setDeductions([...deductions, deduction])
      setExpandedSection(BudgetSection.Deductions, true)
      setExpandedCard(deduction.id, true)
    }
  }

  const handleSaveDeduction = (deduction: Deduction) => {
    const index = deductions.findIndex((e: Deduction) => e.id === deduction.id)
    if (index >= 0) {
      deductions[index] = deduction
      setDeductions([...deductions])
    }

    if (onChange) {
      onChange(deduction)
    }
  }

  const handleDeleteDeduction = (deleted: Deduction) => {
    const index = deductions.findIndex((e: Deduction) => e.id === deleted.id)
    if (index >= 0) {
      deductions.splice(index, 1)
    }
    setDeductions([...deductions])
  }

  const handleAddTax = (index: number) => {
    const option = taxMenuOptions[index]
    if (currentModel && option) {
      const tax = new Tax({
        id: createUUID(),
        accountId: currentModel.accountId,
        userId: currentModel.userId,
        modelId: currentModel.id,
        description: option.description,
        taxType: option.taxType,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: getISODateStartOfYear(),
        amount: 0,
        amountDate: getISODateToday(),
        archived: false,
        sortOrder: taxes.length > 0 ? taxes[taxes.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setTaxes([...taxes, tax])
      setExpandedSection(BudgetSection.Taxes, true)
      setExpandedCard(tax.id, true)
    }
  }

  const handleSaveTax = (tax: Tax) => {
    const index = taxes.findIndex((e: Tax) => e.id === tax.id)
    if (index >= 0) {
      taxes[index] = tax
      setTaxes([...taxes])
    }

    if (onChange) {
      onChange(tax)
    }
  }

  const handleDeleteTax = (deleted: Tax) => {
    const index = taxes.findIndex((e: Tax) => e.id === deleted.id)
    if (index >= 0) {
      taxes.splice(index, 1)
    }
    setTaxes([...taxes])
  }
  
  const handleAddExpense = (index: number, expenseMenuOptions: Expense[], section: BudgetSection, expenseGroup: Expense[]) => {
    const option = expenseMenuOptions[index]
    if (currentModel && option) {
      const expense = new Expense({
        id: createUUID(),
        accountId: currentModel.accountId,
        userId: currentModel.userId,
        modelId: currentModel.id,
        description: option.description,
        expenseCategory: option.expenseCategory,
        discretionary: option.discretionary === true,
        schedule: Schedule.getDefaultSchedule(ScheduleNames.Monthly),
        start: getISODateStartOfYear(),
        amount: 0,
        amountDate: getISODateToday(),
        archived: false,
        sortOrder: expenseGroup.length > 0 ? expenseGroup[expenseGroup.length-1].sortOrder + ModelStore.orderInterval : 0
      })
      setExpenses([...expenses, expense])
      if (expense.discretionary) {
        setDiscretionaryExpenses([...discretionaryExpenses, expense])
      } else {
        setEssentialExpenses([...essentialExpenses, expense])
      }
      setExpandedSection(section, true)
      setExpandedCard(expense.id, true)
    }
  }

  const handleSaveExpense = (expense: Expense) => {
    const index = expenses.findIndex((e: Expense) => e.id === expense.id)
    if (index >= 0) {
      expenses[index] = expense
      setExpenses([...expenses])
    }
    if (onChange) {
      onChange(expense)
    }
  }

  const handleDeleteExpense = (deleted: Expense) => {
    const index = expenses.findIndex((e: Expense) => e.id === deleted.id)
    if (index >= 0) {
      expenses.splice(index, 1)
    }
    setExpenses([...expenses])
  }

  const getIncomeMenuOptions = () => {
    return incomeMenuOptions.map((item: Income) => item.description)
  }

  const getDeductionMenuOptions = () => {
    let options: string[] = []
    if (currentModel) {
      const assets = currentModel.getAssetsByCategory(AssetCategory.LiquidInvestableAssets)
        .filter((a: Asset) => {
          // Filter out assets that already have a deduction
          return (!a.archived && currentModel.deductions.find((d: Deduction) => d.assetId === a.id) === undefined)
        })
      options = assets.map((a: Asset) => a.description)
    }
    return [...options, 'Other']
  }

  // const deductionMenuOptions = [
  //   "Retirement (401k)",
  //   "Other"
  // ]
  //
  const getTaxMenuOptions = () => {
    return taxMenuOptions.map((item: Tax) => item.description)
  }


  const handleChangeYear = (year: number) => {
    setCurrentYear(year)
  }

  const handleChangeMonth = (month?: number) => {
    setCurrentMonth(month)
    if (month !== undefined) {
      setChartView(BudgetChartView.MonthBudget)
    } else {
      setChartView(BudgetChartView.MonthlyBudget)
    }
  }

  const renderYearSelector = () => {
    if (startYear > 0) {
      return (
        <BasicMenuButton
          icon={<ExpandMoreIcon sx={{color: theme.palette.primary.main}}/>}
          label={
            <Typography variant="h3" color="primary" sx={{padding:0, margin:0}}>
              {currentYear}
            </Typography>
          }
          labelPosition="left"
          options={range(startYear, endYear).map((year: number) => year.toString())}
          value={currentYear - startYear}
          onClickOption={(index: number) => {
            handleChangeYear(startYear + index)
          }}
        />
      )

    } else {
      return null
    }
  }

  const renderNetIncomeSelector = () => {
    if (startYear > 0) {
      return (
        <BasicMenuButton
          icon={<ExpandMoreIcon sx={{color: theme.palette.primary.main, width:18, height:18}}/>}
          label={
            <FormLabel sx={styles.formLabel}>{NetIncomeViewNames[netIncomeView]}</FormLabel>
          }
          labelPosition="left"
          options={NetIncomeViewNames}
          value={netIncomeView}
          onClickOption={(index: number) => {
            setNetIncomeView(index)
          }}
        />
      )

    } else {
      return null
    }
  }

  const renderNetIncome = () => {
    let netIncome = 0
    if (currentYearCalc && currentBudgetCalc) {
      netIncome = currentBudgetCalc.totalIncome - currentBudgetCalc.totalDeductions - currentBudgetCalc.totalExpenses
      if (currentBudgetCalc.totalEstimatedTaxes) {
        netIncome -= currentBudgetCalc.totalEstimatedTaxes
      } else {
        netIncome -= currentBudgetCalc.totalTaxes
      }
    }

    const color = (netIncome < 0) ? "red" : "green"
    const income = (netIncomeView === NetIncomeView.MonthlyAvg) ? Math.round(netIncome / 12) : netIncome
    return (
      <Typography variant="h4" color={color}>{numberToMoneyFormat(income, 0)}</Typography>
    )
  }

  const renderUnfoldMenu = () => {
    const allSectionsExpanded = getAllSectionsExpanded()
    const allCardsExpanded = getAllCardsExpanded()

    let icon
    if (allSectionsExpanded) {
      if (allCardsExpanded) {
        icon = <UnfoldLessIcon/>
      } else {
        icon = <UnfoldMoreDoubleIcon/>
      }
    } else {
      icon = <UnfoldMoreIcon/>
    }
    return (
      <IconButton sx={{marginLeft:0, paddingLeft:"4px", paddingRight:0}}
                  onClick={() => {
                    if (allSectionsExpanded) {
                      if (allCardsExpanded) {
                        setAllCardsExpanded(false)
                        setAllSectionsExpanded(false)
                      } else {
                        setAllCardsExpanded(true)
                      }
                    } else {
                      setAllSectionsExpanded(true)
                    }
                  }}
      >
        {icon}
      </IconButton>
    )
  }

  // Print related

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve;
        setIsPrinting(true)
      })
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null;
      setIsPrinting(false)
    }
  })

  const getReportDetails = () => {
    const user = modelStore.currentUser
    if (user && currentModel) {
      const fullDate = dateToLocalFormat(new Date(), "MMM d, yyyy")
      return (
        <React.Fragment>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Date:</FormLabel></Box>
            <Box><Typography variant="body2">{fullDate}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Name:</FormLabel></Box>
            <Box><Typography variant="body2">{user!.fullName}</Typography></Box>
          </Box>
          <Box display="flex" alignItems="baseline">
            <Box width={50}><FormLabel sx={styles.formLabel}>Model:</FormLabel></Box>
            <Box><Typography variant="body2">{currentModel!.name} ({currentModel!.appliedPlan?.name})</Typography></Box>
          </Box>
        </React.Fragment>
      )
    } else {
      return null
    }
  }

  const renderViewMenu = () => {
    let onPrint
    if (chartView === BudgetChartView.BudgetReport) {
      onPrint = (view: number) => {
        handlePrint()
      }
    }

    return (
      <ViewMenu
        view={chartView}
        variant="auto"
        options={BudgetChartNames}
        onChange={(index: number) => {
          setChartView(index)
          Tracking.event({action: "Budget View", label: BudgetChartNames[index]})
          if (index === BudgetChartView.MonthBudget && currentMonth === undefined) {
            setCurrentMonth(0)
          }
        }}
        onPrint={onPrint}
      />
    )
  }

  const renderSlider = () => {
    if (chartView !== BudgetChartView.MonthBudget && chartView !== BudgetChartView.BudgetHistory) {
      return (
        <YearSlider
          value={currentYear}
          min={startYear}
          max={endYear}
          onChange={(year: number) => {
            setCurrentYear(year)
          }}
        />
      )
    } else if (chartView === BudgetChartView.MonthBudget && currentMonth !== undefined) {
      return (
        <MonthSlider
          value={currentMonth}
          onChange={(month: number) => {
            setCurrentMonth(month)
          }}
        />
      )
    } else {
      return null
    }
  }

  const renderListWrapper = (children: ReactNode, providedMain: DroppableProvided) => {
    return (
      <div ref={providedMain.innerRef} {...providedMain.droppableProps}>
        {children}
      </div>
    )
  }

  const filterItem = (item: IBudget) => {
    if (currentSnapshot && currentSnapshot.isCurrent && !showArchived && item.createdAt) {
      // If current snapshot, just look at whether the item is archived
      if (item.archived) {
        return true
      }
    } else if (currentSnapshot && !currentSnapshot.isCurrent) {
      // else show item if it is in the snapshot regardless of the archive status
      const detail = currentSnapshot.getSnapshotDetail(item)
      if (!detail) {
        return true
      }
    }
    return false
  }

  // Income handlers
  
  const renderIncome = (income: Income, provided: DraggableProvided): ReactElement => {
    if (filterItem(income)) {
      return (
        <div ref={provided.innerRef} {...provided.draggableProps}>
        </div>
      )
    }

    return (
      <div ref={provided.innerRef} {...provided.draggableProps}>
        <IncomeCard income={income} model={currentModel!} snapshot={currentSnapshot}
                    projected={getProjectedIncome(income)}
                    expanded={getExpandedCard(income.id)}
                    onChange={(event: SyntheticEvent, value: boolean) => setExpandedCard(income.id, value)}
                    onSave={handleSaveIncome} onDelete={handleDeleteIncome} onArchive={handleSaveIncome}
                    dragHandleProps={!isLocked ? provided.dragHandleProps : undefined}
        />
      </div>
    )
  }

  const handleIncomeDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const srcIndex = result.source.index
    const dstIndex = result.destination.index
    let length
    let sortOrder
    if (result.source.droppableId === "incomes") {
      length = incomes.length
      if (incomes[0].sortOrder) {
        if (dstIndex === 0) {
          // Beginning of list - Set to halfway between 0 and first item
          sortOrder = incomes[0].sortOrder / 2
        } else if (dstIndex >= length-1) {
          // End of list - Set to last item + orderInterval
          sortOrder = incomes[length - 1].sortOrder + ModelStore.orderInterval
        } else {
          // Middle of list - Set to halfway between the adjacent item orders
          sortOrder = incomes[dstIndex-1].sortOrder + (incomes[dstIndex].sortOrder - incomes[dstIndex-1].sortOrder) / 2
        }
        let income = incomes[srcIndex]
        // Move before async operation to avoid extra refresh
        arrayMove(incomes, srcIndex, dstIndex)
        const input: UpdateIncomeInput = {
          id: income.id,
          accountId: income.accountId,
          sortOrder: sortOrder
        }
        await modelStore.updateIncome(input)
      } else {
        // Reorder incomes
        arrayMove(incomes, srcIndex, dstIndex)
        setIncomes(await modelStore.reorderIncomes(incomes))
      }
    }
  }

// Deduction handlers

  const renderDeduction = (deduction: Deduction, provided: DraggableProvided): ReactElement => {
    if (filterItem(deduction)) {
      return (
        <div ref={provided.innerRef} {...provided.draggableProps}>
        </div>
      )
    }

    return (
      <div ref={provided.innerRef} {...provided.draggableProps}>
        <DeductionCard deduction={deduction} model={currentModel!} snapshot={currentSnapshot}
                    projected={getProjectedDeduction(deduction)}
                    expanded={getExpandedCard(deduction.id)}
                    onChange={(event: SyntheticEvent, value: boolean) => setExpandedCard(deduction.id, value)}
                    onSave={handleSaveDeduction} onDelete={handleDeleteDeduction} onArchive={handleSaveDeduction}
                    dragHandleProps={!isLocked ? provided.dragHandleProps : undefined}
        />
      </div>
    )
  }

  const handleDeductionDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const srcIndex = result.source.index
    const dstIndex = result.destination.index
    let length
    let sortOrder
    if (result.source.droppableId === "deductions") {
      length = deductions.length
      if (deductions[0].sortOrder) {
        if (dstIndex === 0) {
          // Beginning of list - Set to halfway between 0 and first item
          sortOrder = deductions[0].sortOrder / 2
        } else if (dstIndex >= length-1) {
          // End of list - Set to last item + orderInterval
          sortOrder = deductions[length - 1].sortOrder + ModelStore.orderInterval
        } else {
          // Middle of list - Set to halfway between the adjacent item orders
          sortOrder = deductions[dstIndex-1].sortOrder + (deductions[dstIndex].sortOrder - deductions[dstIndex-1].sortOrder) / 2
        }
        let deduction = deductions[srcIndex]
        // Move before async operation to avoid extra refresh
        arrayMove(deductions, srcIndex, dstIndex)
        const input: UpdateDeductionInput = {
          id: deduction.id,
          accountId: deduction.accountId,
          sortOrder: sortOrder
        }
        await modelStore.updateDeduction(input)
      } else {
        // Reorder deductions
        arrayMove(deductions, srcIndex, dstIndex)
        setDeductions(await modelStore.reorderDeductions(deductions))
      }
    }
  }

  // Tax handlers

  const renderTax = (tax: Tax, provided: DraggableProvided): ReactElement => {
    if (filterItem(tax)) {
      return (
        <div ref={provided.innerRef} {...provided.draggableProps}>
        </div>
      )
    }

    return (
      <div ref={provided.innerRef} {...provided.draggableProps}>
        <TaxCard tax={tax} model={currentModel!} snapshot={currentSnapshot}
                       projected={getProjectedTax(tax)}
                       expanded={getExpandedCard(tax.id)}
                       onChange={(event: SyntheticEvent, value: boolean) => setExpandedCard(tax.id, value)}
                       onSave={handleSaveTax} onDelete={handleDeleteTax} onArchive={handleSaveTax}
                       dragHandleProps={!isLocked ? provided.dragHandleProps : undefined}
        />
      </div>
    )
  }

  const handleTaxDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const srcIndex = result.source.index
    const dstIndex = result.destination.index
    let length
    let sortOrder
    if (result.source.droppableId === "taxes") {
      length = taxes.length
      if (taxes[0].sortOrder) {
        if (dstIndex === 0) {
          // Beginning of list - Set to halfway between 0 and first item
          sortOrder = taxes[0].sortOrder / 2
        } else if (dstIndex >= length-1) {
          // End of list - Set to last item + orderInterval
          sortOrder = taxes[length - 1].sortOrder + ModelStore.orderInterval
        } else {
          // Middle of list - Set to halfway between the adjacent item orders
          sortOrder = taxes[dstIndex-1].sortOrder + (taxes[dstIndex].sortOrder - taxes[dstIndex-1].sortOrder) / 2
        }
        let tax = taxes[srcIndex]
        // Move before async operation to avoid extra refresh
        arrayMove(taxes, srcIndex, dstIndex)
        const input: UpdateTaxInput = {
          id: tax.id,
          accountId: tax.accountId,
          sortOrder: sortOrder
        }
        await modelStore.updateTax(input)
      } else {
        // Reorder taxes
        arrayMove(taxes, srcIndex, dstIndex)
        setTaxes(await modelStore.reorderTaxes(taxes))
      }
    }
  }

  // Expense handlers

  const renderExpense = (expense: Expense, provided: DraggableProvided): ReactElement => {
    if (filterItem(expense)) {
      return (
        <div ref={provided.innerRef} {...provided.draggableProps}>
        </div>
      )
    }

    return (
      <div ref={provided.innerRef} {...provided.draggableProps}>
        <ExpenseCard expense={expense} model={currentModel!} /*key={expense.id}*/
                 projected={getProjectedExpense(expense)}
                 expanded={getExpandedCard(expense.id)}
                 onChange={(event: SyntheticEvent, value: boolean) => setExpandedCard(expense.id, value)}
                 onSave={handleSaveExpense} onDelete={handleDeleteExpense} onArchive={handleSaveExpense}
                 dragHandleProps={!isLocked ? provided.dragHandleProps : undefined}
        />
      </div>
    )
  }

  const handleExpenseDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return
    }

    let srcIndex = result.source.index
    let dstIndex = result.destination.index
    let expenseGroup: Expense[]
    let setExpenseGroup = setHousingExpenses
    let length: number
    let sortOrder
    switch (result.source.droppableId) {
      case "housingExpenses":
        expenseGroup = housingExpenses
        setExpenseGroup = setHousingExpenses
        break
      case "loanAndLiabilityExpenses":
        expenseGroup = loanAndLiabilityExpenses
        setExpenseGroup = setLoanAndLiabilityExpenses
        break
      case "foodAndPersonalCareExpenses":
        expenseGroup = foodAndPersonalCareExpenses
        setExpenseGroup = setFoodAndPersonalCareExpenses
        break
      case "insuranceAndMedicalExpenses":
        expenseGroup = insuranceAndMedicalExpenses
        setExpenseGroup = setHousingExpenses
        break
      case "vehiclesAndTransportationExpenses":
        expenseGroup = vehiclesAndTransportationExpenses
        setExpenseGroup = setVehiclesAndTransportationExpenses
        break
      case "travelAndEntertainmentExpenses":
        expenseGroup = travelAndEntertainmentExpenses
        setExpenseGroup = setTravelAndEntertainmentExpenses
        break
      case "givingAndMiscExpenses":
        expenseGroup = givingAndMiscExpenses
        setExpenseGroup = setGivingAndMiscExpenses
        break
      case "generalExpenses":
        expenseGroup = generalExpenses
        setExpenseGroup = setGeneralExpenses
        break
      case "essentialExpenses":
        expenseGroup = essentialExpenses
        setExpenseGroup = setEssentialExpenses
        break
      case "discretionaryExpenses":
        expenseGroup = discretionaryExpenses
        setExpenseGroup = setDiscretionaryExpenses
        break
      default:
        return
    }

    length = expenseGroup.length
    if (expenseGroup[0].sortOrder) {
      if (dstIndex === 0) {
        // Beginning of list - Set to halfway between 0 and first item
        sortOrder = expenseGroup[0].sortOrder / 2
      } else if (dstIndex >= length-1) {
        // End of list - Set to last item + orderInterval
        sortOrder = expenseGroup[length - 1].sortOrder + ModelStore.orderInterval
      } else {
        // Middle of list - Set to halfway between the adjacent item orders
        sortOrder = expenseGroup[dstIndex-1].sortOrder + (expenseGroup[dstIndex].sortOrder - expenseGroup[dstIndex-1].sortOrder) / 2
      }
      let expense = expenseGroup[srcIndex]
      // Move before async operation to avoid extra refresh
      // Reorder expenses
      arrayMove(expenseGroup, srcIndex, dstIndex)
      setExpenseGroup(expenseGroup)
      // const srcId = expenseGroup[srcIndex].id
      // srcIndex = expenses.findIndex((e: Expense) => e.id === srcId)
      // const dstId = expenseGroup[dstIndex].id
      // dstIndex = expenses.findIndex((e: Expense) => e.id === dstId)
      // if (srcIndex >= 0 && dstIndex >= 0) {
      //   arrayMove(expenses, srcIndex, dstIndex)
      // }
      const input: UpdateExpenseInput = {
        id: expense.id,
        accountId: expense.accountId,
        sortOrder: sortOrder
      }
      await modelStore.updateExpense(input)
    } else {
      // Reorder expenses
      const srcId = expenseGroup[srcIndex].id
      srcIndex = expenses.findIndex((e: Expense) => e.id === srcId)
      const dstId = expenseGroup[dstIndex].id
      dstIndex = expenses.findIndex((e: Expense) => e.id === dstId)
      if (srcIndex >= 0 && dstIndex >= 0) {
        arrayMove(expenses, srcIndex, dstIndex)
      }
      setExpenses(await modelStore.reorderExpenses(expenses))
    }
  }

  // Quick Start handlers

  const handleSave = async (item: IModelItem)=> {
    const typename = item.getTypename()
    if (typename === "Income") {
      await handleSaveIncome(item as Income)
      setExpandedSection(BudgetSection.Income, true)
    } else if (typename === "Deduction") {
      await handleSaveDeduction(item as Deduction)
      setExpandedSection(BudgetSection.Deductions, true)
    } else if (typename === "Tax") {
      await handleSaveTax(item as Tax)
      setExpandedSection(BudgetSection.Taxes, true)
    } else if (typename === "Expense") {
      await handleSaveExpense(item as Expense)
      if (budgetType === BudgetType.Detailed) {
        switch ((item as Expense).expenseCategory) {
          case ExpenseCategory.Housing:
            setExpandedSection(BudgetSection.Housing, true)
            break
          case ExpenseCategory.FoodAndPersonalCare:
            setExpandedSection(BudgetSection.FoodAndPersonal, true)
            break
          case ExpenseCategory.InsuranceAndMedical:
            setExpandedSection(BudgetSection.InsuranceAndMedical, true)
            break
          case ExpenseCategory.VehiclesAndTransportation:
            setExpandedSection(BudgetSection.VehiclesAndTransportation, true)
            break
          case ExpenseCategory.TravelAndEntertainment:
            setExpandedSection(BudgetSection.TravelAndEntertainment, true)
            break
          case ExpenseCategory.GivingAndMisc:
            setExpandedSection(BudgetSection.GivingAndMisc, true)
            break
        }
      } else {
        if ((item as Expense).discretionary) {
          setExpandedSection(BudgetSection.DiscretionaryExpenses, true)
        } else {
          setExpandedSection(BudgetSection.EssentialExpenses, true)
        }
      }
    } else if (typename === "Model" && onChange) {
      onChange(item as Model)
    }
  }

  const handleNext = (settings: IQuickStartSettings) => {
    const {view} = settings

    if (quickStartUserSetting) {
      const input: UpdateUserSettingInput = {
        id: quickStartUserSetting.id,
        settings: JSON.stringify(settings)
      }
      userStore.updateUserSetting(input)
        .then((userSetting?: UserSetting) => {
          if (userSetting) {
            if (view === "budget") {
              setQuickStartUserSetting(userSetting)
              setQuickStartStep(userSetting.settings["step"])
            } else if (view === "networth") {
              setQuickStartUserSetting(undefined)
              setQuickStartStep(undefined)
              setChartView(BudgetChartView.AnnualBudget)
              setShowQuickStart(false)
              navigate(`${RoutesConfig.calc.pathname}/${view}`)

            }
          }
        })
    }
  }

  const handleExit = async(settings: IQuickStartSettings) => {
    if (quickStartUserSetting) {
      if ((settings.status === "Exited" || settings.status === "Completed") && userStore.isAdvisorOrAdmin) {
        await userStore.deleteUserSetting(quickStartUserSetting.id)
      } else {
        const input: UpdateUserSettingInput = {
          id: quickStartUserSetting.id,
          settings: JSON.stringify(settings)
        }
        await userStore.updateUserSetting(input)
      }
      setQuickStartUserSetting(undefined)
      setQuickStartStep(undefined)
      setShowQuickStart(false)
      setChartView(BudgetChartView.AnnualBudget)
      if (settings.status === "Completed") {
        navigate(`${RoutesConfig.calc.pathname}/dashboard`)
      }
    }
  }

  const toggleMobileChart = () => {
    setMobileChart(mobileChart !== -1 ? -1 : BudgetChartView.AnnualBudget)
  }

  const leftPanelWidth = isMobile ? "100%" : 360
  const rightPanelWidth = isMobile ? 0 : width - 360 - 36

  return (
    <Box sx={{display:"flex", flexDirection:"row", mt:"4px", minHeight: "calc(100vh - 180px)"}}>
      <Box sx={{display:"flex", flexGrow:0, width: { xs:"100%", sm:leftPanelWidth},
        minWidth: { xs:"100%", sm:leftPanelWidth}, minHeight:"100%", maxHeight: "calc(100vh - 84px)",
        overflowY:"scroll", overflowX:"hidden", flexDirection:"column"}}>
        <ProjectionErrorAlert message={calcError}/>
        <Box sx={{display:"flex", mt:0, pl:0, pb:0, mb:"2px", justifyContent:"stretch", alignItems:"center"}}>
          {isMobile && <MobileDrawerButton onClick={toggleDrawer}/>}
          <Typography variant="h2" color="primary">Budget</Typography>
          {isMobile &&
            <Box sx={{display:"flex", flexGrow:1, justifyContent:"flex-end", alignItems:"center", pr:1}}>
              <IconButton onClick={toggleMobileChart}>
                <BarChartIcon color="primary"/>
              </IconButton>
            </Box>
          }
        </Box>
        {isMobile && currentBudgetCalc && mobileChart === BudgetChartView.AnnualBudget &&
          <Box height={220} mt={0} pt={0} sx={{transform: 'scale(1.1, 1.1) '}}>
            <AnnualBudgetWidget budgetCalc={currentBudgetCalc} view={"Annual"} height="220" width="360"/>
          </Box>
        }
        <Box sx={{display:"flex", flexGrow:0, flexDirection:"row", justifyContent:"space-between", paddingLeft: 0.5, paddingRight: 0.5, maxHeight: 50}}>
          <Box sx={{display:"flex", flexGrow:0, flexDirection:"column"}}>
            <Box sx={{display:"flex", flexGrow:0, justifyContent:"space-between", maxHeight:16, marginBottom:"4px"}}>
              <FormLabel sx={styles.formLabel}>Year</FormLabel>
            </Box>
            <Box sx={{display:"flex", flexGrow:0}}>
              {renderYearSelector()}
            </Box>
          </Box>
          <Box sx={{display:"flex", flexGrow:1, flexDirection:"row", justifyContent:"flex-end"}}>
            <Button size="small" variant={showArchived ? "contained" : "outlined"} sx={showArchived ? styles.miniButton : styles.miniButtonDisabled}
                    onClick={(event:any) => {setShowArchived(!showArchived)}}>
              Archived
            </Button>
          </Box>
          <Box sx={{display:"flex", flexGrow:1, flexDirection:"column", alignItems:"flex-end", textAlign:"right"}}>
            {renderNetIncomeSelector()}
            {renderNetIncome()}
          </Box>
          <Box sx={{display:"flex", flexGrow:0, paddingTop:"4px"}}>
            {renderUnfoldMenu()}
          </Box>
        </Box>

        <Section title="Income" total={getSectionTotal(totalIncome)}
                 expanded={getExpandedSection(BudgetSection.Income)}
                 onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.Income, value)}
                 button={
                   <BasicMenuButton
                     icon={<AddButtonIcon locked={isLocked}/>}
                     label=""
                     disabled={isLocked}
                     options={getIncomeMenuOptions()}
                     onClickOption={handleAddIncome}
                   />
                 }
        >
          <DragDropContext onDragEnd={handleIncomeDragEnd}>
            <DraggableList droppableId="incomes" data={incomes} renderWrapper={renderListWrapper}
                         renderItem={renderIncome}
            />
          </DragDropContext>
        </Section>

        <Section title="Deductions & Contributions" total={getSectionTotal(totalDeductions)}
                 titleSx={{letterSpacing: "-0.05em"}}
                 expanded={getExpandedSection(BudgetSection.Deductions)}
                 onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.Deductions, value)}
                 button={
                   <BasicMenuButton
                     icon={<AddButtonIcon locked={isLocked}/>}
                     label=""
                     disabled={isLocked}
                     options={getDeductionMenuOptions()}
                     onClickOption={handleAddDeduction}
                   />
                 }
        >
          <DragDropContext onDragEnd={handleDeductionDragEnd}>
            <DraggableList droppableId="deductions" data={deductions} renderWrapper={renderListWrapper}
                           renderItem={renderDeduction}
            />
          </DragDropContext>
        </Section>

        <Section title="Tax Withholdings" total={getSectionTotal(totalTaxes)}
                 expanded={getExpandedSection(BudgetSection.Taxes)}
                 onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.Taxes, value)}
                 button={
                   <BasicMenuButton
                     icon={<AddButtonIcon locked={isLocked}/>}
                     label=""
                     disabled={isLocked}
                     options={getTaxMenuOptions()}
                     onClickOption={handleAddTax}
                   />
                 }
        >
          <DragDropContext onDragEnd={handleTaxDragEnd}>
            <DraggableList droppableId="taxes" data={taxes} renderWrapper={renderListWrapper}
                           renderItem={renderTax}
            />
          </DragDropContext>
        </Section>

        {budgetType === BudgetType.Basic &&
          <Section title="Essential Expenses" total={getSectionTotal(totalEssentialExpenses)}
                   expanded={getExpandedSection(BudgetSection.EssentialExpenses)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.EssentialExpenses, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={essentialExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, essentialExpenseOptions, BudgetSection.EssentialExpenses, essentialExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="essentialExpenses" data={essentialExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Basic &&
          <Section title="Discretionary Expenses" total={getSectionTotal(totalDiscretionaryExpenses)}
                   expanded={getExpandedSection(BudgetSection.DiscretionaryExpenses)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.DiscretionaryExpenses, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={discretionaryExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, discretionaryExpenseOptions, BudgetSection.DiscretionaryExpenses, discretionaryExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="discretionaryExpenses" data={discretionaryExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="General Expenses" total={getSectionTotal(totalGeneral)}
                   expanded={getExpandedSection(BudgetSection.General)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.General, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={generalExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, generalExpenseOptions, BudgetSection.General, generalExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="generalExpenses" data={generalExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="Housing" total={getSectionTotal(totalHousing)}
                   expanded={getExpandedSection(BudgetSection.Housing)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.Housing, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={housingExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, housingExpenseOptions, BudgetSection.Housing, housingExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="housingExpenses" data={housingExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="Loans & Liabilities" total={getSectionTotal(totalLoansAndLiabilities)}
                   expanded={getExpandedSection(BudgetSection.LoansAndLiabilities)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.LoansAndLiabilities, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={loanAndLiabilityExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, loanAndLiabilityExpenseOptions, BudgetSection.LoansAndLiabilities, loanAndLiabilityExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="loanAndLiabilityExpenses" data={loanAndLiabilityExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="Food & Personal Care" total={getSectionTotal(totalFoodAndPersonal)}
                   expanded={getExpandedSection(BudgetSection.FoodAndPersonal)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.FoodAndPersonal, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={foodAndPersonalCareExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, foodAndPersonalCareExpenseOptions, BudgetSection.FoodAndPersonal, foodAndPersonalCareExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="foodAndPersonalCareExpenses" data={foodAndPersonalCareExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="Insurance & Medical" total={getSectionTotal(totalInsuranceAndMedical)}
                   expanded={getExpandedSection(BudgetSection.InsuranceAndMedical)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.InsuranceAndMedical, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={insuranceAndMedicalExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, insuranceAndMedicalExpenseOptions, BudgetSection.InsuranceAndMedical, insuranceAndMedicalExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="insuranceAndMedicalExpenses" data={insuranceAndMedicalExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="Vehicles & Transportation" total={getSectionTotal(totalVehiclesAndTransportation)}
                   expanded={getExpandedSection(BudgetSection.VehiclesAndTransportation)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.VehiclesAndTransportation, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={vehiclesAndTransportationExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, vehiclesAndTransportationExpenseOptions, BudgetSection.VehiclesAndTransportation, vehiclesAndTransportationExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="vehiclesAndTransportationExpenses" data={vehiclesAndTransportationExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="Travel & Entertainment" total={getSectionTotal(totalTravelAndEntertainment)}
                   expanded={getExpandedSection(BudgetSection.TravelAndEntertainment)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.TravelAndEntertainment, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={travelAndEntertainmentExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, travelAndEntertainmentExpenseOptions, BudgetSection.TravelAndEntertainment, travelAndEntertainmentExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="travelAndEntertainmentExpenses" data={travelAndEntertainmentExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        {budgetType === BudgetType.Detailed &&
          <Section title="Giving & Misc" total={getSectionTotal(totalGivingAndMisc)}
                   expanded={getExpandedSection(BudgetSection.GivingAndMisc)}
                   onChange={(event: SyntheticEvent, value: boolean) => setExpandedSection(BudgetSection.GivingAndMisc, value)}
                   button={
                     <BasicMenuButton
                       icon={<AddButtonIcon locked={isLocked}/>}
                       label=""
                       disabled={isLocked}
                       options={givingAndMiscExpenseOptions.map((expense: Expense) => expense.description)}
                       onClickOption={(index: number) => handleAddExpense(index, givingAndMiscExpenseOptions, BudgetSection.GivingAndMisc, givingAndMiscExpenses)}
                     />
                   }
          >
            <DragDropContext onDragEnd={handleExpenseDragEnd}>
              <DraggableList droppableId="givingAndMiscExpenses" data={givingAndMiscExpenses} renderWrapper={renderListWrapper}
                             renderItem={renderExpense}
              />
            </DragDropContext>
          </Section>
        }

        <Box display="flex" justifyContent="space-between">
          <Typography variant="body2" sx={{pt:"4px"}}>* Discretionary</Typography>
          {!showQuickStart &&
            <BasicMenuButton
              icon={<ArrowDropDownIcon sx={{color: theme.palette.primary.main}}/>}
              label={`${budgetType} Budget`}
              labelPosition="left"
              options={Object.values(BudgetType)}
              value={budgetType === BudgetType.Detailed ? 1 : 0}
              disabled={isLocked}
              onClickOption={async (index: number) => {
                model.setSetting(ModelSetting.BudgetType, Object.values(BudgetType)[index])
                if (currentModel) {
                  await modelStore.saveSettings(currentModel)
                }
                setBudgetType(Object.values(BudgetType)[index])
              }}
            />
          }
        </Box>
      </Box>
      {!isMobile &&
      <Box sx={{
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        width: rightPanelWidth,
        maxWidth: rightPanelWidth,
        minHeight: "100%",
        maxHeight: "calc(100vh - 84px)",
        overflowY:"scroll"
      }}>
        {chartView !== BudgetChartView.QuickStart &&
          <>
            <Box display="flex" flexDirection="column" alignContent="left" pl={2} height={50} mb="0px">
              <FormLabel sx={styles.formLabel}>View</FormLabel>
              {renderViewMenu()}
            </Box>
            <Box sx={{display:"flex", flexGrow:0, paddingLeft:"24px", paddingRight:0, paddingTop:"14px"}}>
              {renderSlider()}
            </Box>
          </>
        }

        <Box display="flex" flexDirection="column" pl={2} mb={2}>
          {currentBudgetCalc &&
            <React.Fragment>
              {chartView === BudgetChartView.QuickStart && currentModel &&
                <BudgetQuickStart step={quickStartStep}
                                    model={currentModel}
                                    snapshot={selectedSnapshot}
                                    quickStartUserSetting={quickStartUserSetting}
                                    onSave={(item: IModelItem) => handleSave(item)}
                                    onNext={handleNext}
                                    onExit={handleExit}
                />
              }
              {chartView === BudgetChartView.AnnualBudget && currentYearCalc &&
                <AnnualBudgetApexChart futureYear={currentYearCalc} budgetCalc={currentBudgetCalc} view={netIncomeView === NetIncomeView.MonthlyAvg ? "MonthlyAvg" : "Annual"} budgetType={budgetType}/>
              }
              {chartView === BudgetChartView.MonthBudget && currentYearCalc &&
                <MonthBudgetApexChart futureYear={currentYearCalc} budgetCalc={currentBudgetCalc} month={currentMonth ?? 0} budgetType={budgetType} onChange={handleChangeMonth}/>
              }
              {chartView === BudgetChartView.MonthlyBudget &&
                <MonthlyBudgetApexChart budgetCalc={currentBudgetCalc} onChange={handleChangeMonth}/>
              }
              {chartView === BudgetChartView.BudgetReport && currentModel &&
                <Box ref={printRef}>
                  <PrintReport isPrinting={isPrinting}
                               title={`${currentYear} Budget Report`}
                               details={getReportDetails()}
                  >
                    <BudgetReport user={modelStore.currentUser!}
                                  model={model}
                                  incomes={incomes}
                                  deductions={deductions}
                                  taxes={taxes}
                                  essentialExpenses={essentialExpenses}
                                  discretionaryExpenses={discretionaryExpenses}
                                  generalExpenses={generalExpenses}
                                  housingExpenses={housingExpenses}
                                  loanAndLiabilityExpenses={loanAndLiabilityExpenses}
                                  foodAndPersonalCareExpenses={foodAndPersonalCareExpenses}
                                  insuranceAndMedicalExpenses={insuranceAndMedicalExpenses}
                                  vehiclesAndTransportationExpenses={vehiclesAndTransportationExpenses}
                                  travelAndEntertainmentExpenses={travelAndEntertainmentExpenses}
                                  givingAndMiscExpenses={givingAndMiscExpenses}
                                  budgetCalc={currentBudgetCalc}
                                  budgetType={budgetType}
                              />
                  </PrintReport>
                </Box>
              }
              {chartView === BudgetChartView.BudgetHistory && currentModel && thisYearCalc &&
                <BudgetHistoryChart model={currentModel}
                                    thisYearCalc={thisYearCalc}
                                    incomes={incomes}
                                    deductions={deductions}
                                    taxes={taxes}
                                    expenses={expenses}
                                    housingExpenses={housingExpenses}
                                    loanAndLiabilityExpenses={loanAndLiabilityExpenses}
                                    foodAndPersonalCareExpenses={foodAndPersonalCareExpenses}
                                    insuranceAndMedicalExpenses={insuranceAndMedicalExpenses}
                                    vehiclesAndTransportationExpenses={vehiclesAndTransportationExpenses}
                                    travelAndEntertainmentExpenses={travelAndEntertainmentExpenses}
                                    givingAndMiscExpenses={givingAndMiscExpenses}
                                    onChange={(item: IModelItem) => {
                                      if (onChange) {
                                        onChange(item)
                                      }
                                    }}
                                    selected={selectedSnapshot}
                                    onSelect={(snapshot: Snapshot) => {setSelectedSnapshot(snapshot)}}
                />
              }
            </React.Fragment>
          }
        </Box>
      </Box>
      }
    </Box>
  )
}

export default BudgetView